/**
 * DependencyContainer is a singleton class that maintains the singleton instance of
 * the DependencyContainer and creates all the dependencies using the instance factory.
 */
import { GraphQLClient as GraphQLRequestClient } from 'graphql-request';
import { AppRepository } from 'src/api/app_repository';
import { auth0Client } from 'src/api/auth0_client';
import { GraphqlClient } from 'src/api/graphql_client';
import { MemberRepository } from 'src/api/member_repository';
import { OrderRepository } from 'src/api/order_repository';
import { getConfig } from 'src/config/config';
import { getPlatform, getRegion, getRepoBackend } from 'src/config/environment';
import { OrderDetailDisplayer } from 'src/distribution_platforms/features/order_details';
import { createInstanceFactory } from 'src/distribution_platforms/features/utils/instance_factory';

export class DependencyContainer {
  private static instance: DependencyContainer | undefined;

  private constructor(
    public graphqlClient: GraphqlClient,
    public orderRepository: OrderRepository,
    public orderDetailDisplayer: OrderDetailDisplayer,
    public uploadWorker: Worker,
    public appRepository: AppRepository,
    public memberRepository: MemberRepository,
  ) {}

  /**
   * Initialize the DependencyContainer
   * Returns a promise that resolves when initialization is complete
   */
  static initialize(): DependencyContainer {
    // If already initialized, return immediately
    if (this.instance) {
      return this.instance;
    }

    // Initialize all dependencies
    const apiUrl = `${getConfig().apiBaseUrl}/graphql`;
    const graphqlRequestClient = new GraphQLRequestClient(apiUrl);
    GraphqlClient.initialize(
      apiUrl,
      async () => auth0Client.getTokenSilently(),
      graphqlRequestClient,
    );
    const instanceFactory = createInstanceFactory(
      getPlatform(),
      getRepoBackend(),
    );
    const orderRepository = instanceFactory.createOrderRepository(
      getRegion(),
      GraphqlClient.instance!,
    );
    const appRepository = instanceFactory.createAppRepository(
      GraphqlClient.instance!,
    );
    const memberRepository = instanceFactory.createMemberRepository(
      GraphqlClient.instance!,
    );

    this.instance = new DependencyContainer(
      GraphqlClient.instance!,
      orderRepository,
      instanceFactory.createProstateBiopsyOrderDetailDisplayer(orderRepository),
      instanceFactory.createUploadWorker(),
      appRepository,
      memberRepository,
    );

    return this.instance;
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): DependencyContainer {
    if (!this.instance) {
      return this.initialize();
    }

    return this.instance;
  }
}
